import React, { useState } from 'react';

const CATEGORY_COLORS = {
  absence: 'badge-warning',
  discipline: 'badge-error',
  medical: 'badge-info',
  financial: 'badge-secondary',
  general: 'badge-ghost',
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'parent', label: 'Parents' },
  { key: 'staff', label: 'Teachers' },
];

export default function EmailInboxList({ emails, isLoading, onSelectThread }) {
  const [filter, setFilter] = useState('all');

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className="loading loading-spinner loading-md" />
      </div>
    );
  }

  // Group by threadId
  const threads = Object.values(
    emails.reduce((acc, email) => {
      const key = email.gmailThreadId || email.id;
      if (!acc[key]) acc[key] = { ...email, messages: [] };
      acc[key].messages.push(email);
      return acc;
    }, {})
  );

  // Apply sender type filter
  const filtered = filter === 'all'
    ? threads
    : threads.filter(t => (t.senderType || 'parent') === filter);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-base-300">
        <div className="flex gap-1">
          {FILTERS.map(f => (
            <button
              key={f.key}
              className={`btn btn-xs ${filter === f.key ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-base-content/40">{filtered.length} thread{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="text-3xl mb-2">📭</div>
            <p className="text-sm text-base-content/50">
              {filter === 'all' ? 'No emails yet.' : `No ${filter === 'parent' ? 'parent' : 'teacher'} emails yet.`}
            </p>
          </div>
        ) : (
          filtered.map((thread) => (
            <button
              key={thread.gmailThreadId || thread.id}
              className="w-full text-left px-4 py-3 hover:bg-base-200 border-b border-base-300/50 transition-colors"
              onClick={() => onSelectThread?.(thread.gmailThreadId, thread.studentId)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {!thread.isRead && (
                      <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                    )}
                    <p className="text-sm font-medium truncate">{thread.subject || '(no subject)'}</p>
                  </div>
                  <p className="text-xs text-base-content/60 truncate mt-0.5">
                    {thread.fromName || thread.fromEmail}
                    {thread.studentName ? ` · ${thread.studentName}` : ''}
                  </p>
                  <p className="text-xs text-base-content/40 truncate">{thread.snippet}</p>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <div className="flex gap-1">
                    {(thread.senderType || 'parent') === 'staff' && (
                      <span className="badge badge-xs badge-primary badge-outline">staff</span>
                    )}
                    <span className={`badge badge-xs ${CATEGORY_COLORS[thread.category] || 'badge-ghost'}`}>
                      {thread.category}
                    </span>
                  </div>
                  {thread.receivedAt?.toDate && (
                    <span className="text-[10px] text-base-content/40">
                      {thread.receivedAt.toDate().toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
